<template>
  <div class="unload-media-component">
    <!-- 标题区域 -->
    <div class="header">
      <span class="title">{{ title }}</span>
      <span class="subtitle">({{ currentCount }}/{{ maxCount }})</span>
      <div class="file-type-tabs" v-if="supportVideo">
        <span
          class="tab-item"
          :class="{ active: fileType === 'image' }"
          @click="switchFileType('image')"
        >
          图片
        </span>
        <span
          class="tab-item"
          :class="{ active: fileType === 'video' }"
          @click="switchFileType('video')"
        >
          视频
        </span>
      </div>
    </div>

    <!-- 媒体文件列表 -->
    <div class="media-list">
      <!-- 已上传媒体项 -->
      <div
        class="media-item"
        v-for="(item, index) in fileList"
        :key="item.id || index"
      >
        <!-- 图片预览 -->
        <img
          v-if="item.type === 'image'"
          :src="item.path"
          class="image-preview"
          @click="previewMedia(index)"
        />
        <!-- 视频预览 -->
        <div v-else-if="item.type === 'video'" class="video-preview" @click="playVideoInFullscreen(item, index)">
          <!-- 视频封面 -->
          <div class="video-cover" v-if="!item.playing">
            <img
              v-if="item.coverPath"
              :src="item.coverPath"
              class="video-cover-image"
            />
            <div class="video-play-icon" v-else>
              <span class="play-text">▶</span>
            </div>
          </div>
          <div class="video-info" v-if="item.coverLoaded && !item.playing">
            <span class="video-duration" v-if="item.duration">
              {{ formatDuration(item.duration) }}
            </span>
          </div>
        </div>

        <div class="media-actions">
          <div class="delete-btn" @click.stop="deleteMedia(index)">
            <span class="delete-text">×</span>
          </div>
        </div>
        <!-- 上传进度 -->
        <div class="upload-progress" v-if="item.uploading">
          <div class="progress-bar">
            <div
              class="progress-inner"
              :style="{ width: item.progress + '%' }"
            ></div>
          </div>
          <span class="progress-text">{{ item.progress }}%</span>
        </div>

        <!-- 上传失败提示 -->
        <div class="upload-error" v-if="item.error">
          <span class="error-text">上传失败</span>
        </div>
      </div>

      <!-- 上传按钮 -->
      <div
        class="upload-btn"
        v-if="currentCount < maxCount"
        @click="showUploadOptions"
      >
        <div class="add-icon-wrapper">
          <span class="add-icon">+</span>
        </div>
        <span class="btn-text">{{ buttonText }}</span>
      </div>
    </div>

    <!-- 操作菜单（使用 Element UI Dialog 模拟 ActionSheet） -->
    <el-dialog
      title="选择操作"
      :visible.sync="showActionSheet"
      width="360px"
      append-to-body
      :close-on-click-modal="true"
      @close="closeActionSheet"
    >
      <div class="action-list">
        <div
          class="action-item"
          v-for="action in availableActions"
          :key="action.type"
          @click="handleAction(action.type)"
        >
          <span class="action-text">{{ action.text }}</span>
        </div>
      </div>
      <span slot="footer">
        <el-button @click="closeActionSheet">取消</el-button>
      </span>
    </el-dialog>

    <!-- 图片预览弹窗 -->
    <el-dialog
      title="图片预览"
      :visible.sync="imagePreviewVisible"
      width="600px"
      append-to-body
      :close-on-click-modal="true"
    >
      <img :src="previewImageUrl" style="width: 100%;" />
    </el-dialog>

    <!-- 全屏视频播放器（使用自定义遮罩 + video） -->
    <div class="fullscreen-video-mask" v-if="fullscreenVideo.visible" @click="exitFullscreen">
      <div class="fullscreen-video-container" @click.stop>
        <video
          ref="fullscreenVideoRef"
          :src="fullscreenVideo.url"
          class="fullscreen-video"
          autoplay
          controls
          @ended="onVideoEnded"
          @fullscreenchange="onFullscreenChange"
        ></video>
        <div class="fullscreen-close" @click="exitFullscreen">
          <span class="close-icon">×</span>
        </div>
      </div>
    </div>

    <!-- 隐藏的文件选择 input -->
    <input
      type="file"
      ref="fileInput"
      style="display: none"
      :accept="fileAccept"
      :multiple="fileMultiple"
      @change="handleFileChange"
    />
  </div>
</template>

<script>
export default {
  name: 'unloadMedia',
  props: {
    // 最大上传数量
    maxCount: {
      type: Number,
      default: 3
    },
    // 标题
    title: {
      type: String,
      default: '媒体上传'
    },
    // 按钮文字
    buttonText: {
      type: String,
      default: '添加文件'
    },
    // 初始文件列表 (v-model)
    value: {
      type: Array,
      default: () => []
    },
    // 上传接口地址
    uploadUrl: {
      type: String,
      default: ''
    },
    // 上传字段名
    uploadFieldName: {
      type: String,
      default: 'file'
    },
    // 是否自动上传
    autoUpload: {
      type: Boolean,
      default: true
    },
    // 允许的来源类型（PC 端仅支持 file，但保留选项）
    sourceTypes: {
      type: Array,
      default: () => ['album'] // 移除 camera，PC 无法使用
    },
    // 支持的文件类型
    fileTypes: {
      type: Array,
      default: () => ['image']
    },
    // 最大视频时长（秒）
    maxVideoDuration: {
      type: Number,
      default: 60
    },
    // 最大文件大小（MB）
    maxFileSize: {
      type: Number,
      default: 50
    }
  },
  data () {
    return {
      fileList: [],
      showActionSheet: false,
      fileType: 'image',
      fullscreenVideo: {
        visible: false,
        url: '',
        index: -1
      },
      imagePreviewVisible: false,
      previewImageUrl: '',
      // 文件选择控制
      fileAccept: '*',
      fileMultiple: false,
      fileResolve: null,
      updatingFromParent: false // 新增
    }
  },
  computed: {
    currentCount () {
      return this.fileList.length
    },
    supportVideo () {
      return this.fileTypes.includes('video')
    },
    availableActions () {
      const actions = []
      // PC 端只支持从文件选择，保留 'album' 选项作为“从本地上传”
      if (this.sourceTypes.includes('album')) {
        actions.push({
          type: 'album',
          text: this.fileType === 'image' ? '选择图片' : '选择视频'
        })
      }
      // 如果还想支持摄像头（PC 通常不支持），但可以保留，但实际无法使用
      // 这里忽略 camera
      return actions
    }
  },
  watch: {
    value: {
      immediate: true,
      handler (newVal) {
      // 将当前 fileList 中需要比较的字段提取出来，避免循环引用
        const getCompareData = (list) => {
          return list.map(item => ({
            id: item.id,
            path: item.path,
            name: item.name,
            type: item.type,
            source: item.source,
            uploadTime: item.uploadTime,
            duration: item.duration,
            size: item.size,
            progress: item.progress,
            uploading: item.uploading,
            uploaded: item.uploaded,
            url: item.url,
            coverLoaded: item.coverLoaded,
            coverPath: item.coverPath,
            playing: item.playing,
            error: item.error
          }))
        }
        const newStr = JSON.stringify(newVal || [])
        const oldStr = JSON.stringify(getCompareData(this.fileList))
        if (newStr !== oldStr) {
          this.updatingFromParent = true
          this.fileList = newVal.map((item, index) => ({
            ...item,
            id: item.id || `file_${Date.now()}_${index}`,
            type: item.type || 'image',
            progress: item.progress || 0,
            uploading: item.uploading || false,
            coverLoaded: item.coverLoaded || false,
            coverPath: item.coverPath || '',
            playing: false,
            error: item.error || false
          }))
          this.$nextTick(() => {
            this.updatingFromParent = false
          })
          // 初始化视频封面（如果支持视频）
          if (this.supportVideo) {
            this.$nextTick(() => {
              this.initVideoCovers()
            })
          }
        }
      }
    },
    fileList (newVal) {
    // 只有非来自父组件更新时才向外 emit
      if (!this.updatingFromParent) {
        this.$emit('input', newVal)
        this.$emit('change', {
          files: newVal,
          count: newVal.length
        })
      }
    }
  },
  methods: {
    // ----- 初始化视频封面（简化：使用第一帧）-----
    initVideoCovers () {
      this.fileList.forEach((file, index) => {
        if (file.type === 'video' && !file.coverLoaded && file.path) {
          this.generateVideoCover(file, index)
        }
      })
    },
    generateVideoCover (file, index) {
      // 使用 video 元素截取第一帧作为封面
      const video = document.createElement('video')
      video.src = file.path
      video.crossOrigin = 'anonymous'
      video.preload = 'metadata'
      video.muted = true
      video.playsInline = true
      video.onloadeddata = () => {
        video.currentTime = 0.1 // 截取0.1秒
      }
      video.onseeked = () => {
        try {
          const canvas = document.createElement('canvas')
          canvas.width = video.videoWidth || 320
          canvas.height = video.videoHeight || 180
          const ctx = canvas.getContext('2d')
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
          const cover = canvas.toDataURL('image/jpeg')
          this.$set(this.fileList, index, {
            ...this.fileList[index],
            coverPath: cover,
            coverLoaded: true,
            duration: video.duration || this.fileList[index].duration
          })
        } catch (e) {
          console.warn('生成视频封面失败:', e)
          this.$set(this.fileList, index, {
            ...this.fileList[index],
            coverLoaded: true
          })
        }
        // 清理
        video.src = ''
        video.load()
      }
      video.onerror = () => {
        this.$set(this.fileList, index, {
          ...this.fileList[index],
          coverLoaded: true
        })
        video.src = ''
        video.load()
      }
      video.load()
    },

    // ----- 文件选择辅助 -----
    openFilePicker () {
      return new Promise((resolve, reject) => {
        this.fileResolve = resolve
        this.$refs.fileInput.value = ''
        this.$refs.fileInput.click()
        setTimeout(() => reject(new Error('取消选择')), 60000)
      })
    },
    handleFileChange (e) {
      const files = e.target.files
      if (files && files.length) {
        this.fileResolve && this.fileResolve(Array.from(files))
      } else {
        this.fileResolve && this.fileResolve([])
      }
      this.fileResolve = null
      this.$refs.fileInput.value = ''
    },

    // ----- 切换文件类型 -----
    switchFileType (type) {
      this.fileType = type
    },

    // ----- 显示操作菜单 -----
    showUploadOptions () {
      if (this.availableActions.length === 1) {
        this.handleAction(this.availableActions[0].type)
        return
      }
      this.showActionSheet = true
    },
    closeActionSheet () {
      this.showActionSheet = false
    },

    // ----- 处理操作 -----
    handleAction (type) {
      this.closeActionSheet()
      if (type === 'album') {
        if (this.fileType === 'image') {
          this.chooseFromAlbum()
        } else {
          this.chooseVideoFromAlbum()
        }
      }
      // 其他类型（如 camera）在 PC 端忽略
    },

    // ----- 选择图片（相册/本地） -----
    chooseFromAlbum () {
      const remain = this.maxCount - this.currentCount
      if (remain <= 0) {
        this.$message.warning(`最多选择 ${this.maxCount} 个文件`)
        return
      }
      this.fileAccept = 'image/*'
      this.fileMultiple = true
      this.openFilePicker().then(files => {
        // 限制数量
        const selected = files.slice(0, remain)
        const newFiles = selected.map((file, idx) => ({
          id: `image_${Date.now()}_${idx}`,
          path: URL.createObjectURL(file),
          name: file.name,
          type: 'image',
          source: 'album',
          uploadTime: new Date().toISOString(),
          progress: 0,
          uploading: false,
          uploaded: false,
          url: '',
          coverLoaded: true,
          size: file.size
        }))
        this.addFilesToMediaList(newFiles, 'album', 'image')
      }).catch(() => {})
    },

    // ----- 选择视频（本地） -----
    chooseVideoFromAlbum () {
      const remain = this.maxCount - this.currentCount
      if (remain <= 0) {
        this.$message.warning(`最多选择 ${this.maxCount} 个文件`)
        return
      }
      this.fileAccept = 'video/*'
      this.fileMultiple = false // 一次选一个
      this.openFilePicker().then(files => {
        if (files.length === 0) return
        const file = files[0]
        // 检查大小
        const maxBytes = this.maxFileSize * 1024 * 1024
        if (file.size > maxBytes) {
          this.$message.error(`视频大小不能超过 ${this.maxFileSize}MB`)
          return
        }
        // 获取时长（通过创建 video 元素）
        const video = document.createElement('video')
        video.src = URL.createObjectURL(file)
        video.preload = 'metadata'
        video.onloadedmetadata = () => {
          const duration = video.duration
          if (duration > this.maxVideoDuration) {
            this.$message.error(`视频时长不能超过 ${this.maxVideoDuration} 秒`)
            URL.revokeObjectURL(video.src)
            return
          }
          // 创建新文件项
          const newFile = {
            id: `video_${Date.now()}`,
            path: URL.createObjectURL(file),
            name: file.name,
            type: 'video',
            source: 'album',
            uploadTime: new Date().toISOString(),
            duration: duration,
            size: file.size,
            progress: 0,
            uploading: false,
            uploaded: false,
            url: '',
            coverLoaded: false,
            coverPath: '',
            playing: false
          }
          this.addFilesToMediaList([newFile], 'album', 'video')
          // 异步生成封面
          this.$nextTick(() => {
            const idx = this.fileList.findIndex(f => f.id === newFile.id)
            if (idx !== -1) {
              this.generateVideoCover(this.fileList[idx], idx)
            }
          })
          URL.revokeObjectURL(video.src)
        }
        video.onerror = () => {
          this.$message.error('无法读取视频信息')
          URL.revokeObjectURL(video.src)
        }
        video.load()
      }).catch(() => {})
    },

    // ----- 添加文件到列表 -----
    addFilesToMediaList (newFiles, source, fileType) {
      this.fileList = [...this.fileList, ...newFiles]
      if (this.autoUpload && this.uploadUrl) {
        // 如果有上传地址，可调用上传方法
        // this.uploadFiles(newFiles);
      }
      this.$emit('add', {
        files: newFiles,
        source: source,
        type: fileType
      })
    },

    // ----- 预览图片 -----
    previewMedia (index) {
      const file = this.fileList[index]
      if (file.type === 'image') {
        this.previewImageUrl = file.path
        this.imagePreviewVisible = true
      }
    },

    // ----- 删除媒体 -----
    deleteMedia (index) {
      this.$confirm('确定要删除这个文件吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        const deletedFile = this.fileList[index]
        this.fileList.splice(index, 1)
        this.$emit('delete', {
          file: deletedFile,
          index: index
        })
      }).catch(() => {})
    },

    // ----- 全屏播放视频 -----
    playVideoInFullscreen (file, index) {
      if (file.type !== 'video') return
      this.fullscreenVideo = {
        visible: true,
        url: file.path,
        index: index
      }
      // 标记播放状态
      this.$set(this.fileList, index, {
        ...file,
        playing: true
      })
      // 自动全屏（如果浏览器支持）
      this.$nextTick(() => {
        const videoEl = this.$refs.fullscreenVideoRef
        if (videoEl && videoEl.requestFullscreen) {
          videoEl.requestFullscreen().catch(() => {})
        }
      })
    },
    exitFullscreen () {
      const idx = this.fullscreenVideo.index
      if (idx !== -1 && this.fileList[idx]) {
        this.$set(this.fileList, idx, {
          ...this.fileList[idx],
          playing: false
        })
      }
      // 退出全屏
      if (document.fullscreenElement) {
        document.exitFullscreen().catch(() => {})
      }
      this.fullscreenVideo = { visible: false, url: '', index: -1 }
    },
    onFullscreenChange (e) {
      if (!document.fullscreenElement) {
        this.exitFullscreen()
      }
    },
    onVideoEnded () {
      this.exitFullscreen()
    },

    // ----- 格式化时长 -----
    formatDuration (duration) {
      const minutes = Math.floor(duration / 60)
      const seconds = Math.floor(duration % 60)
      return `${minutes}:${seconds.toString().padStart(2, '0')}`
    },

    // ----- 外部方法：上传等（占位）-----
    uploadFiles (files) {
      // 可扩展
    }
  }
}
</script>

<style scoped>
.unload-media-component {
  background-color: #fff;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
  border: 1px solid #ebeef5;
}

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  flex-wrap: wrap;
}

.title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.subtitle {
  font-size: 14px;
  color: #909399;
}

.file-type-tabs {
  display: flex;
  background: #f0f2f5;
  border-radius: 4px;
  padding: 2px;
  margin-top: 8px;
  width: 100%;
}

.tab-item {
  flex: 1;
  text-align: center;
  padding: 6px 0;
  font-size: 14px;
  color: #606266;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
}

.tab-item.active {
  background: #fff;
  color: #409eff;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
}

.media-list {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.media-item {
  width: 120px;
  height: 120px;
  border-radius: 8px;
  overflow: hidden;
  position: relative;
  background-color: #f5f7fa;
  border: 1px solid #dcdfe6;
}

.image-preview {
  width: 100%;
  height: 100%;
  object-fit: cover;
  cursor: pointer;
}

.video-preview {
  width: 100%;
  height: 100%;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #000;
  cursor: pointer;
}

.video-cover {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #2c3e50;
}

.video-cover-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.video-play-icon {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
}

.play-text {
  color: #fff;
  font-size: 20px;
  margin-left: 4px;
}

.video-info {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 4px 8px;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.6));
  display: flex;
  justify-content: flex-end;
}

.video-duration {
  color: #fff;
  font-size: 12px;
  background: rgba(0, 0, 0, 0.5);
  padding: 2px 6px;
  border-radius: 3px;
}

.media-actions {
  position: absolute;
  top: 4px;
  right: 4px;
}

.delete-btn {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.delete-text {
  color: #fff;
  font-size: 18px;
  font-weight: bold;
  line-height: 1;
}

.upload-progress {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(0, 0, 0, 0.7);
  padding: 4px 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.progress-bar {
  height: 4px;
  width: 100%;
  background: rgba(255, 255, 255, 0.3);
  border-radius: 2px;
  overflow: hidden;
  margin-bottom: 2px;
}

.progress-inner {
  height: 100%;
  background: #409eff;
  border-radius: 2px;
  transition: width 0.3s;
}

.progress-text {
  color: #fff;
  font-size: 11px;
}

.upload-error {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(245, 108, 108, 0.8);
  padding: 4px;
  text-align: center;
}

.error-text {
  color: #fff;
  font-size: 11px;
}

.upload-btn {
  width: 120px;
  height: 120px;
  border: 1px dashed #dcdfe6;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: #fafbfc;
  cursor: pointer;
  transition: all 0.2s;
}

.upload-btn:hover {
  border-color: #409eff;
  background-color: #ecf5ff;
}

.add-icon-wrapper {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #409eff;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 6px;
}

.add-icon {
  color: #fff;
  font-size: 24px;
  font-weight: bold;
  line-height: 1;
}

.btn-text {
  font-size: 13px;
  color: #606266;
}

/* 操作菜单（dialog 内部） */
.action-list {
  padding: 8px 0;
}
.action-item {
  padding: 12px 0;
  text-align: center;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
}
.action-item:hover {
  background: #f5f7fa;
}
.action-item:last-child {
  border-bottom: none;
}
.action-text {
  font-size: 16px;
  color: #303133;
}

/* 全屏视频遮罩 */
.fullscreen-video-mask {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
}
.fullscreen-video-container {
  width: 100%;
  height: 100%;
  position: relative;
}
.fullscreen-video {
  width: 100%;
  height: 100%;
  background: #000;
}
.fullscreen-close {
  position: absolute;
  top: 20px;
  right: 20px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 2100;
}
.close-icon {
  color: #fff;
  font-size: 28px;
  font-weight: bold;
}
</style>
